import { useEffect, useState } from "react"

import { RolePermission } from "__generated__/graphql"
import DialogProvider from "@components/dialog_provider"
import { AiHelpArticle, AiHelpArticleDraft } from "@domain/ai_help"
import useTranslate from "@hooks/use_translate"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material"

const PERMISSIONS: string[] = Object.values(RolePermission)
const SLUG = /^\/[a-z0-9-]+$/

type EditArticleDialogProps = {
  /** `null` closes it; `"new"` writes an article from scratch. */
  article: AiHelpArticle | "new" | null
  onClose: () => void
  onSave: (draft: AiHelpArticleDraft) => Promise<boolean>
}

const draftOf = (article: AiHelpArticle | "new" | null): AiHelpArticleDraft =>
  !article || article === "new"
    ? {
        slug: "/",
        title: "",
        summary: "",
        category: "",
        panelPath: "",
        permissions: [],
        sections: [{ heading: "", text: "" }],
        faqs: []
      }
    : {
        _id: article._id,
        slug: article.slug,
        title: article.title,
        summary: article.summary ?? "",
        category: article.category ?? "",
        panelPath: article.panelPath ?? "",
        permissions: article.permissions ?? [],
        sections: article.sections.map(({ key, heading, text }) => ({
          key,
          heading,
          text
        })),
        faqs: article.faqs.map(({ question, answer }) => ({ question, answer }))
      }

export default function EditArticleDialog({
  article,
  onClose,
  onSave
}: EditArticleDialogProps) {
  const { translate } = useTranslate()
  const [draft, setDraft] = useState<AiHelpArticleDraft>(draftOf(null))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setDraft(draftOf(article))
  }, [article])

  const isNew = article === "new"
  const set = (patch: Partial<AiHelpArticleDraft>) =>
    setDraft((current) => ({ ...current, ...patch }))
  const setSection = (
    index: number,
    patch: Partial<AiHelpArticleDraft["sections"][number]>
  ) =>
    set({
      sections: draft.sections.map((section, at) =>
        at === index ? { ...section, ...patch } : section
      )
    })
  const setFaq = (
    index: number,
    patch: Partial<AiHelpArticleDraft["faqs"][number]>
  ) =>
    set({
      faqs: draft.faqs.map((faq, at) =>
        at === index ? { ...faq, ...patch } : faq
      )
    })

  const validSlug = !isNew || SLUG.test(draft.slug ?? "")
  const validPath = !draft.panelPath || draft.panelPath.startsWith("/")
  const valid =
    validSlug &&
    validPath &&
    !!draft.title.trim() &&
    draft.sections.some((section) => section.heading.trim()) &&
    draft.sections.every(
      (section) => section.heading.trim() || !section.text.trim()
    )

  const save = async () => {
    setLoading(true)
    try {
      if (await onSave(draft)) {
        onClose()
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogProvider
      title={isNew ? translate("ai_help_new_article") : draft.title}
      isOpen={!!article}
      setClose={onClose}
      size="large"
      leftButton={{
        text: translate("ai_help_cancel"),
        color: "secondary",
        onClick: onClose
      }}
      rightButton={{
        text: translate("ai_help_save"),
        color: "primary",
        onClick: save,
        loading,
        disabled: !valid
      }}
    >
      <Stack gap={2} pt={1}>
        <Stack direction="row" gap={2}>
          <TextField
            label={translate("ai_help_slug")}
            value={draft.slug}
            onChange={(event) => set({ slug: event.target.value })}
            disabled={!isNew}
            error={!validSlug}
            helperText={translate("ai_help_slug_hint")}
            fullWidth
          />
          <TextField
            label={translate("ai_help_category")}
            placeholder="Gelen kutusu"
            value={draft.category}
            onChange={(event) => set({ category: event.target.value })}
            fullWidth
          />
        </Stack>
        <TextField
          label={translate("ai_help_article_title")}
          value={draft.title}
          onChange={(event) => set({ title: event.target.value })}
          fullWidth
        />
        <TextField
          label={translate("ai_help_summary")}
          value={draft.summary}
          onChange={(event) => set({ summary: event.target.value })}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction="row" gap={2}>
          <TextField
            label={translate("ai_help_panel_path")}
            placeholder="/tasks"
            value={draft.panelPath}
            onChange={(event) => set({ panelPath: event.target.value })}
            error={!validPath}
            helperText={translate("ai_help_panel_path_hint")}
            fullWidth
          />
          <Autocomplete
            multiple
            fullWidth
            options={PERMISSIONS}
            value={draft.permissions ?? []}
            onChange={(_event, permissions) => set({ permissions })}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate("ai_help_permissions")}
                helperText={translate("ai_help_permissions_hint")}
              />
            )}
          />
        </Stack>

        <Divider />
        <Typography variant="subtitle2">
          {translate("ai_help_sections")}
        </Typography>
        {draft.sections.map((section, index) => (
          <Stack key={section.key ?? `new-${index}`} gap={1}>
            <Stack direction="row" gap={1} alignItems="center">
              <TextField
                size="small"
                label={translate("ai_help_section_heading")}
                value={section.heading}
                onChange={(event) =>
                  setSection(index, { heading: event.target.value })
                }
                fullWidth
              />
              <IconButton
                aria-label={translate("ai_help_remove")}
                disabled={draft.sections.length === 1}
                onClick={() =>
                  set({
                    sections: draft.sections.filter((_, at) => at !== index)
                  })
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              size="small"
              label={translate("ai_help_section_text")}
              value={section.text}
              onChange={(event) =>
                setSection(index, { text: event.target.value })
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        ))}
        <Button
          startIcon={<AddIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={() =>
            set({ sections: [...draft.sections, { heading: "", text: "" }] })
          }
        >
          {translate("ai_help_add_section")}
        </Button>

        <Divider />
        <Typography variant="subtitle2">{translate("ai_help_faqs")}</Typography>
        {draft.faqs.map((faq, index) => (
          <Stack key={index} direction="row" gap={1} alignItems="flex-start">
            <Stack gap={1} flex={1}>
              <TextField
                size="small"
                label={translate("ai_help_faq_question")}
                value={faq.question}
                onChange={(event) =>
                  setFaq(index, { question: event.target.value })
                }
                fullWidth
              />
              <TextField
                size="small"
                label={translate("ai_help_faq_answer")}
                value={faq.answer}
                onChange={(event) =>
                  setFaq(index, { answer: event.target.value })
                }
                multiline
                minRows={2}
                fullWidth
              />
            </Stack>
            <IconButton
              aria-label={translate("ai_help_remove")}
              onClick={() =>
                set({ faqs: draft.faqs.filter((_, at) => at !== index) })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          startIcon={<AddIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={() =>
            set({ faqs: [...draft.faqs, { question: "", answer: "" }] })
          }
        >
          {translate("ai_help_add_faq")}
        </Button>
        {!isNew && (
          <Typography variant="caption" color="text.secondary">
            {translate("ai_help_edit_hint")}
          </Typography>
        )}
      </Stack>
    </DialogProvider>
  )
}
